import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications,
  markAllNotificationsAsRead
} from '@/api/notifications';
import Button from '@/components/Button';
import Breadcrumb from '@/components/Breadcrumb';
import { formatNotificationTime } from '@/utils/formatUtils';
import dondonGreyLogo from '@/assets/dondon_grey.svg';
import notificationIcon from '@/assets/notification.svg';
import promotionIcon from '@/assets/promotion.svg';
import orderIcon from '@/assets/order.svg';
import sellIcon from '@/assets/sell.svg';
import profileIcon from '@/assets/profile.svg';
import warningBlackIcon from '@/assets/warning_black.svg';
import { getProductThumbnailUrl } from '@/utils/imageUtils';

const NotificationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('ALL');
  const [cursor, setCursor] = useState(null); // Only used for triggering next page fetch, doesn't reflect latest pagination status
  const [categoryNotifications, setCategoryNotifications] = useState({});
  const [categoryLoadingStates, setCategoryLoadingStates] = useState({});
  const [categoryCursors, setCategoryCursors] = useState({});

  // Query for notifications
  const { data: notificationsData, isLoading, isError, isFetching, error } = useQuery({
    queryKey: ['notifications', activeTab, cursor],
    queryFn: () => {
      return getNotifications({
        category: activeTab,
        cursor,
        limit: 20
      });
    },
    enabled: true, // Always enable to allow invalidation to work
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error) => {
      console.error('Failed to fetch notifications:', error);
    },
  });

  // Mutation for marking all notifications as read
  const markAllAsReadMutation = useMutation({
    mutationFn: markAllNotificationsAsRead,
    onMutate: async () => {
      // Optimistically update all notifications to read
      setCategoryNotifications(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(category => {
          if (updated[category]) {
            updated[category] = updated[category].map(notification => ({
              ...notification,
              isRead: true
            }));
          }
        });
        return updated;
      });
    },
    onSuccess: () => {
      console.log('NotificationsPage: Marking all notifications as read succeeded, invalidating queries...');
      // Invalidate and refetch notification queries
      queryClient.invalidateQueries({ queryKey: ['notificationUnreadCount'] });
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
        refetchType: 'active' // Only refetch active queries
      });
      console.log('NotificationsPage: Query invalidation completed');
    },
    onError: (error) => {
      console.error('Failed to mark all notifications as read:', error);
      // Revert optimistic update on error - refetch to get correct state
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
        refetchType: 'active'
      });
    },
  });



  // Update category notifications when new data arrives
  useEffect(() => {
    if (notificationsData?.data && Array.isArray(notificationsData.data)) {
      const isLoadingMore = cursor !== null;

      console.log('Processing notifications data:', {
        activeTab,
        isLoadingMore,
        cursor,
        dataLength: notificationsData.data.length,
        responseCursor: notificationsData.cursor
      });

      setCategoryNotifications(prev => {
        if (isLoadingMore) {
          // Append new notifications for pagination
          const existingCount = prev[activeTab]?.length || 0;
          console.log(`Appending ${notificationsData.data.length} notifications to existing ${existingCount}`);
          return {
            ...prev,
            [activeTab]: [...(prev[activeTab] || []), ...notificationsData.data]
          };
        } else {
          // Replace notifications for new category or initial load/refresh
          console.log(`Replacing notifications for ${activeTab} with ${notificationsData.data.length} items`);
          return {
            ...prev,
            [activeTab]: notificationsData.data
          };
        }
      });

      // Update cursor for this category - use the cursor from response or null
      setCategoryCursors(prev => ({
        ...prev,
        [activeTab]: notificationsData.cursor || null
      }));

      // Mark this category as loaded
      setCategoryLoadingStates(prev => ({
        ...prev,
        [activeTab]: false
      }));
    } else if (notificationsData && !notificationsData.data) {
      // Handle case where API returns success but no data property
      console.warn('Notifications API returned unexpected data structure:', notificationsData);
      if (cursor === null) {
        setCategoryNotifications(prev => ({
          ...prev,
          [activeTab]: []
        }));
      }
      // Update cursor to null since no data
      setCategoryCursors(prev => ({
        ...prev,
        [activeTab]: null
      }));
      // Mark this category as loaded even for empty data
      setCategoryLoadingStates(prev => ({
        ...prev,
        [activeTab]: false
      }));
    } else if (isError) {
      console.error('Notifications query error:', error);
      // Mark this category as failed/loaded
      setCategoryLoadingStates(prev => ({
        ...prev,
        [activeTab]: false
      }));
    }
  }, [notificationsData, cursor, isError, error, activeTab]);

  // Manage cursor and loading state when tab changes
  useEffect(() => {

    // Check if this category has been loaded before
    const categoryHasData = categoryNotifications[activeTab] !== undefined;
    const categoryIsLoading = categoryLoadingStates[activeTab] === true;

    if (!categoryHasData && !categoryIsLoading) {
      // Only set loading state if category hasn't been loaded and isn't currently loading
      setCategoryLoadingStates(prev => ({
        ...prev,
        [activeTab]: true
      }));

      // Set cursor to trigering a loading
      setCursor(null);
    }

    // Fallback timeout to prevent infinite loading state
    const timeout = setTimeout(() => {
      setCategoryLoadingStates(prev => ({
        ...prev,
        [activeTab]: false
      }));
    }, 15000); // 15 seconds timeout

    return () => clearTimeout(timeout);
  }, [activeTab, categoryNotifications, categoryLoadingStates]);

  // Additional effect to handle loading state based on query status
  useEffect(() => {
    // If query is not loading and not fetching, ensure current category loading state is reset
    if (!isLoading && !isFetching) {
      setCategoryLoadingStates(prev => ({
        ...prev,
        [activeTab]: false
      }));
    }
  }, [isLoading, isFetching, activeTab]);

  // Mark all notifications as read when entering the page
  useEffect(() => {
    // Only mark as read once when the component mounts
    markAllAsReadMutation.mutate();
  }, []); // Empty dependency array means this runs only once on mount

  const categories = [
    { id: 'ALL', label: t('notifications.categories.all'), icon: notificationIcon },
    { id: 'PROMOTION', label: t('notifications.categories.promotion'), icon: promotionIcon },
    { id: 'PURCHASE', label: t('notifications.categories.purchase'), icon: orderIcon },
    { id: 'SALE', label: t('notifications.categories.sale'), icon: sellIcon },
    { id: 'ACCOUNT', label: t('notifications.categories.account'), icon: profileIcon },
    { id: 'SYSTEM', label: t('notifications.categories.system'), icon: warningBlackIcon },
  ];

  // Handle category change
  const handleCategoryChange = (categoryId) => {
    if (categoryId !== activeTab) {
      setActiveTab(categoryId);
    }
  };

  // Handle load more
  const handleLoadMore = () => {
    const currentCursor = categoryCursors[activeTab];
    console.log('handleLoadMore called:', { activeTab, currentCursor, categoryCursors });
    if (currentCursor) {
      setCursor(currentCursor);
    }
  };



  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification || !notification.id) {
      console.warn('Invalid notification clicked:', notification);
      return;
    }

    // No need to mark as read on click since all notifications are marked as read when entering the page

    if (notification.navigation) {
      const { type, resourceId, parameters } = notification.navigation;

      try {
        switch (type) {
          case 'ORDER':
            if (resourceId) {
              if (notification.category === 'PURCHASE') {
                // Navigate to orders page with order ID in state
                navigate('/me/orders', { state: { openOrderId: resourceId } });
              } else if (notification.category === 'SALE') {
                // Navigate to sales page with sale ID in state
                navigate('/me/sales', { state: { openSaleId: resourceId } });
              }
            }
            break;
          case 'LISTING':
            if (resourceId) {
              navigate(`/listing/edit/${resourceId}`);
            }
            break;
          case 'WALLET':
            navigate('/me/wallet');
            break;
          case 'PROFILE':
            navigate('/me');
            break;
          case 'EXTERNAL_LINK':
            if (parameters?.url) {
              window.open(parameters.url, '_blank');
            }
            break;
          case 'NONE':
          default:
            break;
        }
      } catch (error) {
        console.error('Error handling notification navigation:', error);
      }
    }
  };

  const currentNotifications = categoryNotifications[activeTab] || [];
  const hasMoreNotifications = Boolean(categoryCursors[activeTab]);
  const activeCategory = categories.find(cat => cat.id === activeTab);
  const isCurrentCategoryLoading = categoryLoadingStates[activeTab] === true;

  // Debug logging
  console.log('NotificationsPage Debug:', {
    activeTab,
    isLoading,
    isFetching,
    isCurrentCategoryLoading,
    categoryLoadingStates,
    categoryNotifications,
    categoryCursors,
    currentNotificationsCount: currentNotifications.length,
    hasMoreNotifications,
    currentCursor: cursor
  });

  const breadcrumbItems = [
    { label: t('common.home'), href: '/' },
    { label: t('notifications.message_center') }
  ];

  return (
    <div className="w-full bg-[#f7f7f7]">
      <div className="container py-6 flex flex-col gap-6">
        <Breadcrumb items={breadcrumbItems} />

        <div className="w-full pb-[72px] flex justify-start items-start gap-6">
          <div className="w-[298px] p-2 bg-white rounded-md flex flex-col justify-start items-start gap-6 overflow-hidden">
            <div className="self-stretch flex flex-col justify-start items-start">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className={`self-stretch pl-4 py-4 rounded flex justify-start items-center gap-3 cursor-pointer ${
                    activeTab === category.id ? 'bg-[#f4f4f4]' : 'hover:bg-[#f9f9f9]'
                  }`}
                  onClick={() => handleCategoryChange(category.id)}
                >
                  <div className="w-4 h-4 relative overflow-hidden">
                    <img src={category.icon} alt={category.label} className="w-4 h-4" />
                  </div>
                  <div className={`${activeTab === category.id ? 'flex-1' : ''} text-[#111111] text-sm font-normal leading-tight`}>
                    {category.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="w-[942px] self-stretch px-6 pb-6 bg-white rounded-md flex flex-col justify-start items-start min-h-[600px]">
            <div className="self-stretch py-6 flex justify-start items-center">
              <div className="flex justify-start items-center gap-2">
                <div className="text-[#111111] text-lg font-bold leading-[27px]">
                  {activeCategory?.label || t('notifications.categories.all')}
                </div>
              </div>
              <div className="flex-1 flex justify-end items-center gap-2.5" />
            </div>

            <div className="self-stretch flex flex-col justify-start items-start gap-4">
              {(isLoading && !cursor) || isCurrentCategoryLoading ? (
                <div className="self-stretch h-[400px] flex justify-center items-center">
                  <span className="text-[#aaaaaa]">{t('common.loading')}</span>
                </div>
              ) : isError ? (
                <div className="self-stretch h-[400px] flex flex-col justify-center items-center gap-4">
                  <span className="text-[#aaaaaa]">{t('common.error')}</span>
                  {error?.message && (
                    <span className="text-[#ff5501] text-sm">{error.message}</span>
                  )}
                  <Button
                    variant="default"
                    size="s"
                    onClick={() => window.location.reload()}
                  >
                    {t('common.retry')}
                  </Button>
                </div>
              ) : currentNotifications.length > 0 ? (
                currentNotifications.map((notification) => {
                  // Validate notification data
                  if (!notification || !notification.id) {
                    console.warn('Invalid notification data:', notification);
                    return null;
                  }

                  return (
                    <div
                      key={notification.id}
                      className="self-stretch pb-4 relative border-b border-[#eeeeee] flex justify-start items-center gap-3 cursor-pointer hover:bg-[#f9f9f9] px-2 py-1 rounded"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="w-14 h-14 relative bg-white border border-[#DDDDDD] rounded overflow-hidden">
                        <img
                          className="w-14 h-14 left-0 top-0 absolute mix-blend-darken object-cover"
                          src={getProductThumbnailUrl(notification.image?.url)}
                          alt=""
                        />
                      </div>
                      <div className="flex-1 flex flex-col justify-start items-start gap-0">
                        <div className="self-stretch text-[#111111] text-sm font-normal leading-[21px] line-clamp-1">
                          {notification.localizedTitle || notification.title || t('notifications.no_title')}
                        </div>
                        <div className="self-stretch flex justify-start items-center gap-3">
                          <div className="flex-1 text-[#aaaaaa] text-xs font-normal leading-[18px] line-clamp-1">
                            {notification.localizedContent || notification.content || ''}
                          </div>
                          <div className="flex-shrink-0 text-right text-[#aaaaaa] text-xs font-normal leading-[18px] whitespace-nowrap">
                            {formatNotificationTime(notification.createdAt)}
                          </div>
                        </div>
                      </div>
                      {!notification.isRead && (
                        <div className="w-2 h-2 left-[50px] top-[-2px] absolute bg-[#ff5501] rounded outline outline-2 outline-white" />
                      )}
                    </div>
                  );
                }).filter(Boolean)
              ) : (
                <div className="self-stretch h-[400px] flex flex-col justify-center items-center gap-6">
                  <img src={dondonGreyLogo} alt="DonDon" className="w-24 h-16 opacity-50" />
                  <div className="text-center text-[#aaaaaa] text-sm font-normal leading-tight">
                    {t('notifications.empty_state')}
                  </div>
                </div>
              )}
            </div>

            {isFetching && cursor && (
              <div className="w-full flex justify-center items-center py-4">
                <p className="text-[#aaaaaa]">{t('common.loading')}</p>
              </div>
            )}

            {hasMoreNotifications && !isFetching && (
              <div className="w-full flex justify-center mt-8">
                <Button
                  variant="default"
                  size="m"
                  onClick={handleLoadMore}
                  disabled={isFetching}
                >
                  {t('home.actions.load_more')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
